/**
 * Pictures a built-in guide step can show above its text, by key
 * (`BuiltinStep.illustration` in the backend). Keys are a closed set here:
 * an unknown key (a newer backend, a typo) draws nothing.
 */
import type { ComponentType } from "react";
import { Box } from "@mantine/core";
import { AlarmScheduleClock, AlarmSiren, AlarmTagReader } from "./alarm";

interface Illustration {
  /** The picture's alt text: what it shows, in plain words. */
  alt: string;
  Component: ComponentType<{ title: string }>;
}

export const ILLUSTRATIONS: Record<string, Illustration> = {
  "alarm-tag-reader": {
    alt: "A tag held up to the door reader, next to the keypad for your code",
    Component: AlarmTagReader,
  },
  "alarm-siren": {
    alt: "An alarm siren making a loud sound, with 15 minutes to act",
    Component: AlarmSiren,
  },
  "alarm-schedule-clock": {
    alt: "A clock at night, with an arrow: the alarm turns on again every 2 hours",
    Component: AlarmScheduleClock,
  },
};

export function hasIllustration(name: string | null | undefined): boolean {
  return !!name && Object.prototype.hasOwnProperty.call(ILLUSTRATIONS, name);
}

/** The step's picture, or nothing for an unknown/empty key. */
export function StepIllustration({ name }: { name: string | null | undefined }) {
  if (!name || !hasIllustration(name)) return null;
  const { alt, Component } = ILLUSTRATIONS[name];
  return (
    <Box h={120} maw={320} mx="auto" w="100%" c="var(--mantine-color-text)">
      <Component title={alt} />
    </Box>
  );
}
